import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Sparkles,
  ImageIcon,
  Store,
  LayoutGrid,
  Bell,
  Heart,
  UserCheck,
  ChevronRight,
  Inbox,
  type LucideIcon,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { Skeleton } from '@/components/atoms/skeleton';
import { cn, formatPrice, getAmountByCurrency } from '@/lib/utils';
import { useConfigStore } from '@/lib/stores/config.store';
import { useCampaigns } from '../../hooks/use-campaigns';
import type { AdType, CampaignStatus, Campaign } from '../../types/ads.types';

// ============ Type config ============

const typeConfig: Record<AdType, { label: string; icon: LucideIcon }> = {
  sponsored_product: { label: 'Sponsorisé', icon: Sparkles },
  homepage_carousel: { label: 'Carousel', icon: ImageIcon },
  featured_shop: { label: 'Boutique', icon: Store },
  category_banner: { label: 'Bannière', icon: LayoutGrid },
  push_all_users: { label: 'Notification', icon: Bell },
  push_shop_subscribers: { label: 'Abonnés', icon: UserCheck },
  push_product_likers: { label: 'Fans', icon: Heart },
  push_single_product_likers: { label: 'Fans produit', icon: Bell },
};

const statusConfig: Record<CampaignStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  active: { label: 'Active', variant: 'default' },
  paused: { label: 'En pause', variant: 'secondary' },
  completed: { label: 'Terminée', variant: 'outline' },
  draft: { label: 'Brouillon', variant: 'outline' },
  disabled_by_admin: { label: 'Désactivée', variant: 'destructive' },
  deleted_by_admin: { label: 'Supprimée', variant: 'destructive' },
};

// ============ Component ============

interface RecentCampaignsListProps {
  limit?: number;
}

export function RecentCampaignsList({ limit = 3 }: RecentCampaignsListProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currency } = useConfigStore();
  const { data: campaignsData, isLoading } = useCampaigns();

  const recentCampaigns = useMemo(() => {
    if (!campaignsData?.campaigns) return [];
    return [...campaignsData.campaigns]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, limit);
  }, [campaignsData, limit]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base font-medium">
            {t('ads.recent.title', 'Campagnes récentes')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="size-8 rounded-lg" />
              <div className="flex-1 space-y-1">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-20" />
              </div>
              <Skeleton className="h-6 w-16" />
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (recentCampaigns.length === 0) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base font-medium">
            {t('ads.recent.title', 'Campagnes récentes')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="p-3 rounded-full bg-muted mb-3">
              <Inbox className="size-6 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">
              {t('ads.recent.empty', 'Aucune campagne pour le moment')}
            </p>
            <Button
              variant="link"
              size="sm"
              onClick={() => navigate('/ads/campaigns/new')}
              className="mt-2"
            >
              {t('ads.recent.createFirst', 'Créer votre première campagne')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getSpent = (campaign: Campaign) => {
    return getAmountByCurrency(currency, {
      eur: campaign.spentEur,
      usd: campaign.spentUsd,
      kmf: campaign.spentKmf,
    });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-base font-medium">
          {t('ads.recent.title', 'Campagnes récentes')}
        </CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/ads/campaigns')}
          className="text-muted-foreground"
        >
          {t('ads.recent.viewAll', 'Voir toutes')}
          <ChevronRight className="size-4 ml-1" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {recentCampaigns.map((campaign) => {
          const typeInfo = typeConfig[campaign.type];
          const statusInfo = statusConfig[campaign.status];
          const Icon = typeInfo?.icon || Sparkles;

          return (
            <div
              key={campaign.refId}
              onClick={() => navigate(`/ads/campaigns/${campaign.refId}`)}
              className={cn(
                'flex items-center gap-3 p-3 rounded-lg cursor-pointer',
                'hover:bg-muted/50 transition-colors'
              )}
            >
              <div className="size-9 rounded-lg bg-muted flex items-center justify-center">
                <Icon className="size-4 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{campaign.name}</p>
                <p className="text-xs text-muted-foreground">
                  {typeInfo?.label || campaign.type}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {formatPrice(getSpent(campaign), currency)}
                </span>
                <Badge variant={statusInfo?.variant || 'outline'} className="text-xs">
                  {statusInfo?.label || campaign.status}
                </Badge>
              </div>
              <ChevronRight className="size-4 text-muted-foreground" />
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
